import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import AddArtistInput from "../../Lineup/AddArtistInput";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from "@chakra-ui/react";
import { usePerformersSearch } from "../../hooks/usePerformersSearch";
import {
  EventDetailScheduledUpdatesSection,
  KYDPerformerDisplay,
  KYDPerformerProfile,
} from "@common/interfaces/KYDEvent";
import { ScheduledUpdateValues } from "../EditScheduledUpdateModal";
import { PerformerRow } from "../../Lineup/PerformerRow";
import TimelineDatepickerFormInput from "../../Timeline/TimelineDatepickerFormInput";
import { FiCheck, FiTrash, FiTrash2, FiWatch } from "react-icons/fi";
import { useCallback, useState } from "react";
import actions from "../../../../actions";
import { Select } from "chakra-react-select";
import { SetTimeButton } from "src/ui/SetTimeButton";
import dayjs from "dayjs";
import KYDTimePicker from "src/ui/KYDTimePicker";

const LineupStep = ({
  mode,
  data,
}: // isLoading = false,
{
  mode: "edit" | "create";
  data: Pick<
    EventDetailScheduledUpdatesSection["data"],
    "id" | "timezone" | "scheduled_updates"
  >;
  isLoading?: boolean;
}) => {
  const {
    setValue,
    watch,
    formState: { isLoading, isSubmitting, defaultValues },
  } = useFormContext<ScheduledUpdateValues>();
  const [isSettingTime, setIsSettingTime] = useState<Record<string, boolean>>(
    {}
  );

  const {
    results: searchResults,
    search,
    loading: searchLoading,
  } = usePerformersSearch(data.id);

  const onAddPerformer = useCallback(
    async ({
      name,
      soundcharts_id,
      image_url,
      profiles,
    }: {
      name: string;
      soundcharts_id?: string;
      image_url?: string;
      profiles?: {
        platform: string;
        url: string;
      }[];
    }) => {
      try {
        const performer = await actions.ADMIN.createPerformer(
          data.id,
          {
            soundcharts_id,
            image_url,
            name,
            profiles,
          },
          true
        );

        return performer || null;
      } catch (err) {
        console.error(err);
      }
    },
    [data]
  );

  const performers = watch("new_performers");

  console.log("tz", data.timezone);

  return (
    <VStack w="full">
      <VStack w="full">
        <VStack w="full" gap={2}>
          <VStack w="full" gap={2}>
            {performers.map((performer) => (
              <Box
                w="full"
                borderRadius={"md"}
                borderWidth={1}
                borderColor={"gray.200"}
                p={2}
                key={performer.id}
              >
                <PerformerRow
                  name={performer.name}
                  image={performer.image_url}
                  id={performer.id}
                  platforms={[
                    performer.instagram as KYDPerformerProfile,
                    performer.spotify as KYDPerformerProfile,
                    performer.tiktok as KYDPerformerProfile,
                  ]}
                  actionsWrapperProps={{
                    gap: [2, 4],
                    maxW: ["100%", "60%"],
                  }}
                  actions={[
                    isSettingTime[performer.id] ? (
                      <HStack>
                        <KYDTimePicker
                          label="Set Time"
                          value={
                            performer.set_time
                              ? dayjs(performer.set_time).tz(data.timezone)
                              : null
                          }
                          onChange={(time) => {
                            const isValid =
                              time &&
                              dayjs(time).tz(data.timezone).isValid() &&
                              dayjs(time).tz(data.timezone);
                            if (!isValid) return;

                            setValue(
                              "new_performers",
                              performers.map((p) =>
                                p.id === performer.id
                                  ? {
                                      ...p,
                                      set_time: time.toISOString(),
                                    }
                                  : p
                              )
                            );
                          }}
                          timezone={data.timezone}
                        />
                        <IconButton
                          icon={<FiCheck />}
                          aria-label="Save time"
                          onClick={() => {
                            setIsSettingTime((prev) => ({
                              ...prev,
                              [performer.id]: false,
                            }));
                          }}
                        />
                      </HStack>
                    ) : (
                      <SetTimeButton
                        setTime={performer.set_time}
                        onSetTime={() => {
                          setIsSettingTime((prev) => ({
                            ...prev,
                            [performer.id]: true,
                          }));
                        }}
                        timezone={data.timezone}
                      />
                    ),
                    <Box backgroundColor="gray.200" w={"1px"} h={6} />,
                    <IconButton
                      icon={<FiTrash2 />}
                      aria-label="Delete performer"
                      onClick={() => {
                        setValue(
                          "new_performers",
                          performers.filter((p) => p.id !== performer.id)
                        );
                      }}
                    />,
                  ]}
                />
              </Box>
            ))}
          </VStack>
        </VStack>
        <Box w="full">
          <AddArtistInput
            isDisabled={isLoading || isSubmitting}
            isLoading={searchLoading}
            onSearch={search}
            searchResults={searchResults}
            onConfirm={async (performer) => {
              const createdPerformer = await onAddPerformer(performer);
              // if (!createdPerformer) return;

              setValue("new_performers", [
                ...performers,
                {
                  ...performer,
                  id: createdPerformer?.id,
                  isNew: true,
                },
              ]);
            }}
          />
        </Box>
      </VStack>
      <VStack w="full" gap={4}>
        {mode === "create" ? (
          <Accordion allowToggle w="full">
            <AccordionItem border="none">
              <AccordionButton>
                <HStack w="full" justifyContent={"start"}>
                  <Text fontWeight="semibold">Schedule Announcement</Text>
                  <Text color={"gray.500"} fontSize={"sm"}>
                    (Optional)
                  </Text>
                </HStack>

                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {data.scheduled_updates.length > 0 ? (
                  <Text color={"gray.500"}>
                    You already have a scheduled update for this event.
                  </Text>
                ) : (
                  <TimelineDatepickerFormInput
                    dateTimeProps={{
                      disabled: isLoading,
                    }}
                    validationFailMessage="Needs to be before event end, and equal to or after doors open."
                    name="date"
                    label=""
                    required
                    timezone={data.timezone}
                    highlights={{}}
                  />
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        ) : (
          <Box py={2} w="full">
            <TimelineDatepickerFormInput
              dateTimeProps={{
                disabled: isLoading,
              }}
              validationFailMessage="Needs to be before event end, and equal to or after doors open."
              name="date"
              label={`Announcement Date`}
              required
              timezone={data.timezone}
              highlights={{}}
            />
          </Box>
        )}
      </VStack>
    </VStack>
  );
};

export default LineupStep;
